import type { JsonValue } from '../../common.types';
import type { CardType } from '../../payment.types';

export interface CardPointeTerminalSaleRequestBody {
    readonly merchantId: string;
    readonly hsn: string;
    readonly amount: string;
    readonly capture: 'true';
    readonly includeSignature?: boolean;
    readonly beep?: boolean;
    readonly orderId?: string;
}

export interface CardPointeTerminalSaleRequestDetails {
    readonly method: 'POST';
    readonly path: '/api/v4/authCard';
    readonly body: CardPointeTerminalSaleRequestBody;
}

export const CardPointeTerminalSaleOutcome = {
    APPROVED: 'approved',
    DECLINED: 'declined',
    CANCELED: 'canceled',
    MALFORMED_RESPONSE: 'malformed_response',
} as const;

export type CardPointeTerminalSaleOutcome =
    (typeof CardPointeTerminalSaleOutcome)[keyof typeof CardPointeTerminalSaleOutcome];

export interface CardPointeTerminalSaleAuthorizationBody {
    readonly respstat: 'A' | 'C';
    readonly amount?: string;
    readonly resptext?: string;
    readonly authcode?: string;
    readonly respcode?: string;
    readonly merchid?: string;
    readonly cvvresp?: string;
    readonly retref?: string;
    readonly avsresp?: string;
    readonly respproc?: string;
    readonly batchid?: string;
    readonly token?: string;
    readonly account?: string;
    readonly expiry?: string;
    readonly name?: string;
    readonly binInfo?: unknown;
    readonly emvTagData?: unknown;
    readonly tokenExpired?: boolean;
    readonly entrymode?: string;
    readonly bintype?: string;
    readonly orderId?: string;
    readonly receiptData?: unknown;
}

export interface CardPointeTerminalSaleErrorBody {
    readonly errorCode: string;
    readonly errorMessage: string;
    readonly authResponse?: CardPointeTerminalSaleAuthorizationBody;
}

// Discriminator: outcome
export type CardPointeTerminalSaleResponseDetails =
    | {
          readonly outcome: typeof CardPointeTerminalSaleOutcome.APPROVED;
          readonly httpStatus: 200;
          readonly body: CardPointeTerminalSaleAuthorizationBody & {
              readonly respstat: 'A';
              readonly retref: string;
              readonly amount: string;
              readonly merchid: string;
          };
      }
    | {
          readonly outcome: typeof CardPointeTerminalSaleOutcome.DECLINED;
          readonly httpStatus: 200;
          readonly body: CardPointeTerminalSaleAuthorizationBody & {
              readonly respstat: 'C';
              readonly resptext: string;
          };
      }
    | {
          readonly outcome: typeof CardPointeTerminalSaleOutcome.CANCELED;
          readonly httpStatus: number;
          readonly body: CardPointeTerminalSaleErrorBody;
      }
    | {
          readonly outcome: typeof CardPointeTerminalSaleOutcome.MALFORMED_RESPONSE;
          readonly httpStatus: number;
          readonly body: unknown;
          readonly message: string;
      };

export const CardPointeTerminalSaleLifecyclePayloadType = {
    REQUEST: 'cardpointe_terminal_sale_request_v1',
    RESPONSE: 'cardpointe_terminal_sale_response_v1',
} as const;

export type CardPointeTerminalSaleLifecyclePayloadType =
    (typeof CardPointeTerminalSaleLifecyclePayloadType)[keyof typeof CardPointeTerminalSaleLifecyclePayloadType];

export interface CardPointeTerminalSaleRequestLifecyclePayload {
    readonly gateway_lifecycle_payload_type: typeof CardPointeTerminalSaleLifecyclePayloadType.REQUEST;
    readonly request_details: CardPointeTerminalSaleRequestDetails;
}

export interface CardPointeTerminalSaleResponseLifecyclePayload {
    readonly gateway_lifecycle_payload_type: typeof CardPointeTerminalSaleLifecyclePayloadType.RESPONSE;
    readonly response_details: CardPointeTerminalSaleResponseDetails;
}

// Discriminator: gateway_lifecycle_payload_type
export type CardPointeTerminalSaleLifecyclePayload =
    | CardPointeTerminalSaleRequestLifecyclePayload
    | CardPointeTerminalSaleResponseLifecyclePayload;

export interface CardPointeTerminalTokenizationRequestBody {
    readonly merchantId: string;
    readonly hsn: string;
    readonly amount: string;
    readonly includeSignature?: boolean;
    readonly beep?: boolean;
}

export interface CardPointeTerminalTokenizationRequestDetails {
    readonly method: 'POST';
    readonly path: '/api/v4/readCard';
    readonly body: CardPointeTerminalTokenizationRequestBody;
}

export interface CardPointeTransactionStatusRequestPathParams {
    readonly orderid: string;
    readonly merchid: string;
    readonly set: '1';
}

export interface CardPointeTransactionStatusRequestDetails {
    readonly method: 'GET';
    readonly pathTemplate: '/cardconnect/rest/inquireByOrderid/{orderid}/{merchid}/{set}';
    readonly pathParams: CardPointeTransactionStatusRequestPathParams;
}

export interface CloverTerminalTokenizationRequestHeaders {
    readonly deviceId: string;
    readonly idempotencyKey?: string;
    readonly posId: string;
    readonly userAgent: string;
    readonly timeoutSeconds?: number;
}

export type CloverCardEntryMethod =
    | 'MAG_STRIPE'
    | 'EMV'
    | 'NFC'
    | 'MANUAL'
    | 'DEFAULT';

export interface CloverTerminalTokenizationRequestBody {
    readonly cardEntryMethods?: readonly CloverCardEntryMethod[];
    readonly tokenType: 'ECOMM_COMPAT';
}

export interface CloverTerminalTokenizationRequestDetails {
    readonly method: 'POST';
    readonly path: '/v1/card/tokens';
    readonly headers: CloverTerminalTokenizationRequestHeaders;
    readonly body: CloverTerminalTokenizationRequestBody;
}

export interface CloverTransactionDetailsRequestHeaders {
    readonly deviceId: string;
    readonly posId: string;
    readonly userAgent: string;
    readonly timeoutSeconds?: number;
}

export interface CloverTransactionStatusRequestDetails {
    readonly method: 'GET';
    readonly path: '/v1/payments/external/{externalPaymentId}';
    readonly externalPaymentId: string;
    readonly headers: CloverTransactionDetailsRequestHeaders;
}

export interface CloverTerminalSaleRequestHeaders {
    readonly deviceId: string;
    readonly idempotencyKey: string;
    readonly posId: string;
    readonly userAgent: string;
    readonly timeoutSeconds?: number;
}

export interface CloverTerminalSaleRequestBody {
    readonly amount: number;
    readonly externalPaymentId: string;
    readonly final: true;
    readonly capture?: true;
    readonly externalReferenceId?: string;
    readonly tipAmount?: number;
}

export interface CloverTerminalSaleRequestDetails {
    readonly method: 'POST';
    readonly path: '/v1/payments';
    readonly headers: CloverTerminalSaleRequestHeaders;
    readonly body: CloverTerminalSaleRequestBody;
}

export const CloverTerminalSaleOutcome = {
    APPROVED: 'approved',
    DECLINED: 'declined',
    CANCELED: 'canceled',
    MALFORMED_RESPONSE: 'malformed_response',
} as const;

export type CloverTerminalSaleOutcome =
    (typeof CloverTerminalSaleOutcome)[keyof typeof CloverTerminalSaleOutcome];

export const CloverPaymentResult = {
    SUCCESS: 'SUCCESS',
    FAIL: 'FAIL',
    INITIATED: 'INITIATED',
    VOIDED: 'VOIDED',
    VOIDING: 'VOIDING',
    VOID_FAILED: 'VOID_FAILED',
    AUTH: 'AUTH',
    AUTH_COMPLETED: 'AUTH_COMPLETED',
    DISCOUNT: 'DISCOUNT',
} as const;

export type CloverPaymentResult =
    (typeof CloverPaymentResult)[keyof typeof CloverPaymentResult];

export interface CloverTerminalSaleCardTransactionBody {
    readonly authCode?: string;
    readonly referenceId?: string;
    readonly state?: string;
    readonly token?: string;
    readonly transactionNo?: string;
    readonly type?: string;
}

export interface CloverTerminalSalePaymentBody {
    readonly id?: string;
    readonly amount?: number;
    readonly result: CloverPaymentResult;
    readonly cardTransaction?: CloverTerminalSaleCardTransactionBody;
    readonly externalPaymentId?: string;
    readonly externalReferenceId?: string;
}

export interface CloverTerminalSaleErrorBody {
    readonly message: string;
    readonly type: string;
    readonly code?: string;
    readonly requestId?: string;
    readonly requestType?: string;
}

// Discriminator: outcome
export type CloverTerminalSaleResponseDetails =
    | {
          readonly outcome: typeof CloverTerminalSaleOutcome.APPROVED;
          readonly httpStatus: 200;
          readonly body: {
              readonly payment: CloverTerminalSalePaymentBody & {
                  readonly id: string;
                  readonly amount: number;
                  readonly result: typeof CloverPaymentResult.SUCCESS;
              };
          };
      }
    | {
          readonly outcome: typeof CloverTerminalSaleOutcome.DECLINED;
          readonly httpStatus: 200;
          readonly body: {
              readonly payment: CloverTerminalSalePaymentBody & {
                  readonly result: typeof CloverPaymentResult.FAIL;
              };
          };
      }
    | {
          readonly outcome: typeof CloverTerminalSaleOutcome.CANCELED;
          readonly httpStatus: 209;
          readonly body: CloverTerminalSaleErrorBody;
      }
    | {
          readonly outcome: typeof CloverTerminalSaleOutcome.MALFORMED_RESPONSE;
          readonly httpStatus: number;
          readonly body: unknown;
          readonly message: string;
      };

export const CloverTerminalSaleLifecyclePayloadType = {
    REQUEST: 'clover_terminal_sale_request_v1',
    RESPONSE: 'clover_terminal_sale_response_v1',
} as const;

export type CloverTerminalSaleLifecyclePayloadType =
    (typeof CloverTerminalSaleLifecyclePayloadType)[keyof typeof CloverTerminalSaleLifecyclePayloadType];

export interface CloverTerminalSaleRequestLifecyclePayload {
    readonly gateway_lifecycle_payload_type: typeof CloverTerminalSaleLifecyclePayloadType.REQUEST;
    readonly request_details: CloverTerminalSaleRequestDetails;
}

export interface CloverTerminalSaleResponseLifecyclePayload {
    readonly gateway_lifecycle_payload_type: typeof CloverTerminalSaleLifecyclePayloadType.RESPONSE;
    readonly response_details: CloverTerminalSaleResponseDetails;
}

// Discriminator: gateway_lifecycle_payload_type
export type CloverTerminalSaleLifecyclePayload =
    | CloverTerminalSaleRequestLifecyclePayload
    | CloverTerminalSaleResponseLifecyclePayload;

export interface CardPointeFundingRequestQuery {
    readonly merchid: string;
    readonly date: string;
    readonly page?: string;
    readonly limit?: string;
}

export interface CardPointeFundingRequestDetails {
    readonly method: 'GET';
    readonly path: '/cardconnect/rest/funding';
    readonly query: CardPointeFundingRequestQuery;
}

export interface CardPointeReconciliationLookupRequest {
    readonly funding_date: string;
    readonly gateway_txn_id: string;
    readonly page?: number;
    readonly limit?: number;
}

export interface CardPointeReconciliationLookupRequestDetails {
    readonly fundingRequestDetails: CardPointeFundingRequestDetails;
    readonly lookup: CardPointeReconciliationLookupRequest;
}

export interface CardPointeRefundRequestBody {
    readonly merchid: string;
    readonly retref: string;
    readonly amount?: string;
    readonly orderid?: string;
}

export interface CardPointeRefundRequestDetails {
    readonly method: 'POST';
    readonly path: '/cardconnect/rest/refund';
    readonly body: CardPointeRefundRequestBody;
}

export interface CardPointeVoidRequestBody {
    readonly merchid: string;
    readonly retref: string;
    readonly amount?: string;
}

export interface CardPointeVoidRequestDetails {
    readonly method: 'POST';
    readonly path: '/cardconnect/rest/void';
    readonly body: CardPointeVoidRequestBody;
}

export const CloverRefundKind = {
    CLOUD_PAY: 'CLOUD_PAY',
    ECOMMERCE: 'ECOMMERCE',
} as const;

export type CloverRefundKind =
    (typeof CloverRefundKind)[keyof typeof CloverRefundKind];

export interface CloverCloudPayRefundRequestHeaders {
    readonly deviceId: string;
    readonly idempotencyKey: string;
    readonly posId: string;
    readonly userAgent: string;
    readonly timeoutSeconds?: number;
}

export interface CloverCloudPayRefundRequestBody {
    readonly amount?: number;
    readonly fullRefund?: boolean;
}

export interface CloverCloudPayRefundRequestDetails {
    readonly kind: typeof CloverRefundKind.CLOUD_PAY;
    readonly method: 'POST';
    readonly path: '/v1/payments/{paymentId}/refunds';
    readonly paymentId: string;
    readonly headers: CloverCloudPayRefundRequestHeaders;
    readonly body: CloverCloudPayRefundRequestBody;
}

export interface CloverEcommerceRefundRequestDetails {
    readonly kind: typeof CloverRefundKind.ECOMMERCE;
    readonly method: 'POST';
    readonly path: '/v1/refunds';
    readonly chargeId: string;
    readonly headers: {
        readonly idempotencyKey: string;
        readonly userAgent: string;
    };
    readonly body: {
        readonly charge: string;
        readonly amount?: number;
    };
}

export type CloverRefundRequestDetails =
    | CloverCloudPayRefundRequestDetails
    | CloverEcommerceRefundRequestDetails;

export const CloverVoidReason = {
    USER_CANCEL: 'USER_CANCEL',
    TRANSPORT_ERROR: 'TRANSPORT_ERROR',
    REJECT_SIGNATURE: 'REJECT_SIGNATURE',
    REJECT_PARTIAL_AUTH: 'REJECT_PARTIAL_AUTH',
    NOT_APPROVED: 'NOT_APPROVED',
    FAILED: 'FAILED',
    AUTH_CLOSED_NEW_CARD: 'AUTH_CLOSED_NEW_CARD',
    DEVELOPER_PAY_PARTIAL_AUTH: 'DEVELOPER_PAY_PARTIAL_AUTH',
    REJECT_DUPLICATE: 'REJECT_DUPLICATE',
    REJECT_OFFLINE: 'REJECT_OFFLINE',
    GIFTCARD_LOAD_FAILED: 'GIFTCARD_LOAD_FAILED',
    USER_GIFTCARD_LOAD_CANCEL: 'USER_GIFTCARD_LOAD_CANCEL',
    DEVELOPER_PAY_TIP_ADJUST_FAILED: 'DEVELOPER_PAY_TIP_ADJUST_FAILED',
    USER_CUSTOMER_CANCEL: 'USER_CUSTOMER_CANCEL',
    FRAUD: 'FRAUD',
} as const;

export type CloverVoidReason =
    (typeof CloverVoidReason)[keyof typeof CloverVoidReason];

export interface CloverVoidRequestHeaders {
    readonly deviceId: string;
    readonly idempotencyKey: string;
    readonly posId: string;
    readonly userAgent: string;
    readonly timeoutSeconds?: number;
}

export interface CloverVoidRequestBody {
    readonly voidReason: CloverVoidReason;
}

export interface CloverCloudPayVoidRequestDetails {
    readonly method: 'POST';
    readonly path: '/v1/payments/{paymentId}/void';
    readonly paymentId: string;
    readonly headers: CloverVoidRequestHeaders;
    readonly body: CloverVoidRequestBody;
}

export interface CloverEcommerceVoidRequestDetails {
    readonly method: 'POST';
    readonly path: '/v1/refunds';
    readonly chargeId: string;
    readonly headers: {
        readonly userAgent: string;
    };
    readonly body: {
        readonly charge: string;
    };
}

// Discriminator: path
export type CloverVoidRequestDetails =
    | CloverCloudPayVoidRequestDetails
    | CloverEcommerceVoidRequestDetails;

export interface CloverReconciliationLookupRequest {
    readonly merchant_transaction_id: string;
    readonly settlement_date?: string;
}

export interface CloverReconciliationLookupRequestDetails {
    readonly statusRequestDetails: CloverTransactionStatusRequestDetails;
    readonly lookup: CloverReconciliationLookupRequest;
}

export interface CloverCnpTokenSaleRequestHeaders {
    readonly userAgent: string;
    readonly forwardedFor: string;
}

export interface CloverCnpTokenSaleFirstStoredCredentials {
    readonly sequence: 'FIRST';
    readonly is_scheduled: false;
    readonly initiator: 'CARDHOLDER';
}

export interface CloverCnpTokenSaleSubsequentStoredCredentials {
    readonly sequence: 'SUBSEQUENT';
    readonly is_scheduled?: boolean;
    readonly initiator?: 'MERCHANT' | 'CARDHOLDER';
}

export type CloverCnpTokenSaleStoredCredentials =
    | CloverCnpTokenSaleFirstStoredCredentials
    | CloverCnpTokenSaleSubsequentStoredCredentials;

export interface CloverCnpTokenSaleRequestBody {
    readonly amount: number;
    readonly currency: string;
    readonly source: string;
    readonly intent?: 'save_credential_on_file';
    readonly capture?: boolean;
    readonly description?: string;
    readonly external_reference_id?: string;
    readonly receipt_email?: string;
    readonly stored_credentials?: CloverCnpTokenSaleStoredCredentials;
}

export interface CloverCnpTokenSaleRequestDetails {
    readonly method: 'POST';
    readonly path: '/v1/charges';
    readonly headers: CloverCnpTokenSaleRequestHeaders;
    readonly body: CloverCnpTokenSaleRequestBody;
}

export interface CardPointeCnpTokenSaleRequestBody {
    readonly merchid: string;
    readonly account: string;
    readonly amount: string;
    readonly currency: string;
    readonly capture: 'y';
    readonly orderid?: string;
    readonly name?: string;
    readonly expiry?: string;
    readonly address?: string;
    readonly city?: string;
    readonly region?: string;
    readonly country?: string;
    readonly postal?: string;
}

export interface CardPointeCnpTokenSaleRequestDetails {
    readonly method: 'PUT';
    readonly path: '/cardconnect/rest/auth';
    readonly body: CardPointeCnpTokenSaleRequestBody;
}

export const PaymentGatewayTerminalSaleStatus = {
    APPROVED: 'approved',
    DECLINED: 'declined',
    CANCELED: 'canceled',
    MALFORMED_RESPONSE: 'malformed_response',
} as const;

export type PaymentGatewayTerminalSaleStatus =
    (typeof PaymentGatewayTerminalSaleStatus)[keyof typeof PaymentGatewayTerminalSaleStatus];

// Discriminator: status
export type PaymentGatewayTerminalSaleResult =
    | {
          readonly status: typeof PaymentGatewayTerminalSaleStatus.APPROVED;
          readonly gatewayTransactionId: string;
          readonly approvedAmount: string;
          readonly authorizationCode?: string;
          readonly batchId?: string;
          readonly maskedAccount?: string;
          readonly gatewayToken?: string;
      }
    | {
          readonly status: typeof PaymentGatewayTerminalSaleStatus.DECLINED;
          readonly message: string;
          readonly gatewayTransactionId?: string;
          readonly responseCode?: string;
      }
    | {
          readonly status: typeof PaymentGatewayTerminalSaleStatus.CANCELED;
          readonly message: string;
          readonly gatewayTransactionId?: string;
      }
    | {
          readonly status: typeof PaymentGatewayTerminalSaleStatus.MALFORMED_RESPONSE;
          readonly message: string;
      };

export const CardPointeLifecyclePreflightGatewayOperation = {
    TERMINAL_SALE: 'terminal_sale',
} as const;

export type CardPointeLifecyclePreflightGatewayOperation =
    (typeof CardPointeLifecyclePreflightGatewayOperation)[keyof typeof CardPointeLifecyclePreflightGatewayOperation];

export interface CardPointeLifecyclePreflightRequestDetails {
    readonly gateway_operation: typeof CardPointeLifecyclePreflightGatewayOperation.TERMINAL_SALE;
    readonly terminal_sale_request_details: CardPointeTerminalSaleRequestDetails;
}

export const CardPointeLifecycleFinalizationGatewayOperation = {
    TERMINAL_SALE: 'terminal_sale',
} as const;

export type CardPointeLifecycleFinalizationGatewayOperation =
    (typeof CardPointeLifecycleFinalizationGatewayOperation)[keyof typeof CardPointeLifecycleFinalizationGatewayOperation];

export interface CardPointeLifecycleFinalizationRequestDetails {
    readonly gateway_operation: typeof CardPointeLifecycleFinalizationGatewayOperation.TERMINAL_SALE;
    readonly terminal_sale_response_details: CardPointeTerminalSaleResponseDetails;
    readonly terminal_sale_result: PaymentGatewayTerminalSaleResult;
}

export const CloverLifecyclePreflightGatewayOperation = {
    TERMINAL_SALE: 'terminal_sale',
} as const;

export type CloverLifecyclePreflightGatewayOperation =
    (typeof CloverLifecyclePreflightGatewayOperation)[keyof typeof CloverLifecyclePreflightGatewayOperation];

export interface CloverLifecyclePreflightRequestDetails {
    readonly gateway_operation: typeof CloverLifecyclePreflightGatewayOperation.TERMINAL_SALE;
    readonly terminal_sale_request_details: CloverTerminalSaleRequestDetails;
}

export const CloverLifecycleFinalizationGatewayOperation = {
    TERMINAL_SALE: 'terminal_sale',
} as const;

export type CloverLifecycleFinalizationGatewayOperation =
    (typeof CloverLifecycleFinalizationGatewayOperation)[keyof typeof CloverLifecycleFinalizationGatewayOperation];

export interface CloverLifecycleFinalizationRequestDetails {
    readonly gateway_operation: typeof CloverLifecycleFinalizationGatewayOperation.TERMINAL_SALE;
    readonly terminal_sale_response_details: CloverTerminalSaleResponseDetails;
    readonly terminal_sale_result: PaymentGatewayTerminalSaleResult;
}

export const GatewayLifecycleRequestType = {
    BIN_LOOKUP: 'bin_lookup_request_v1',
    ACH_PAYMENT: 'ach_payment_request_v1',
    CARDPOINTE_CNP_TOKEN_SALE: 'cardpointe_auth_request_v1',
    CARDPOINTE_LIFECYCLE_FINALIZATION:
        'cardpointe_lifecycle_finalization_request_v1',
    CARDPOINTE_LIFECYCLE_PREFLIGHT:
        'cardpointe_lifecycle_preflight_request_v1',
    CARDPOINTE_RECONCILIATION: 'cardpointe_reconciliation_request_v1',
    CARDPOINTE_REFUND: 'cardpointe_refund_request_v1',
    CARDPOINTE_STORED_CARD_SALE: 'cardpointe_stored_card_sale_request_v1',
    CARDPOINTE_TERMINAL_SALE: 'cardpointe_terminal_sale_request_v1',
    CARDPOINTE_TERMINAL_TOKENIZATION:
        'cardpointe_terminal_tokenization_request_v1',
    CARDPOINTE_TRANSACTION_STATUS:
        'cardpointe_inquire_by_orderid_request_v1',
    CARDPOINTE_VOID: 'cardpointe_void_request_v1',
    CLOVER_CNP_TOKEN_SALE: 'clover_charge_request_v1',
    CLOVER_LIFECYCLE_FINALIZATION:
        'clover_lifecycle_finalization_request_v1',
    CLOVER_LIFECYCLE_PREFLIGHT: 'clover_lifecycle_preflight_request_v1',
    CLOVER_RECONCILIATION: 'clover_reconciliation_request_v1',
    CLOVER_REFUND: 'clover_refund_request_v1',
    CLOVER_STORED_CARD_SALE: 'clover_stored_card_sale_request_v1',
    CLOVER_TERMINAL_SALE: 'clover_terminal_sale_request_v1',
    CLOVER_TERMINAL_TOKENIZATION: 'clover_terminal_tokenization_request_v1',
    CLOVER_TRANSACTION_STATUS: 'clover_payment_external_request_v1',
    CLOVER_VOID: 'clover_void_request_v1',
    INTEGRATION_RECONCILIATION: 'integration_reconcile_request_v1',
    TERMINAL_SALE_DISPATCH: 'terminal_sale_dispatch_request',
    VALOR_CARD_TOKEN_SALE: 'valor_sale_copy_request_v1',
    VALOR_RECONCILIATION: 'valor_reconcile_request_v1',
    VALOR_REFUND: 'valor_refund_offset_sale_v1',
    VALOR_TERMINAL_PUBLISH_V1: 'valor_vc_publish_request_v1',
    VALOR_TERMINAL_PUBLISH_V2: 'valor_vc_publish_request_v2',
    VALOR_TRANSACTION_LIST: 'valor_transaction_list_request_v1',
    VALOR_TRANSACTION_STATUS: 'valor_transaction_status_request_v1',
    VALOR_VOID: 'valor_void_request_v1',
} as const;

export interface ValorBinLookupEvidenceV1 {
    readonly bin_lookup_evidence_version: 1;
    readonly bin_lookup_backend: 'VALOR';
    readonly pan_prefix: string;
    readonly card_type: CardType;
    readonly provider_response: JsonValue;
}

export interface BinLookupApiEvidenceV1 {
    readonly bin_lookup_evidence_version: 1;
    readonly bin_lookup_backend: 'BINLOOKUPAPI';
    readonly pan_prefix: string;
    readonly card_type: CardType;
    readonly provider_response: JsonValue;
}

// Discriminator: bin_lookup_backend
export type BinLookupEvidenceV1 = ValorBinLookupEvidenceV1 | BinLookupApiEvidenceV1;

export type BinLookupGatewayLifecycleRequest = GatewayLifecycleRequestVariant<
    typeof GatewayLifecycleRequestType.BIN_LOOKUP,
    BinLookupEvidenceV1
>;

export type GatewayLifecycleRequestType =
    (typeof GatewayLifecycleRequestType)[keyof typeof GatewayLifecycleRequestType];

export interface CardPointeLifecyclePreflightRequestPayload {
    readonly gateway_lifecycle_payload_type: typeof GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_PREFLIGHT;
    readonly request_details: CardPointeLifecyclePreflightRequestDetails;
}

export interface CardPointeLifecycleFinalizationRequestPayload {
    readonly gateway_lifecycle_payload_type: typeof GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_FINALIZATION;
    readonly request_details: CardPointeLifecycleFinalizationRequestDetails;
}

export interface CloverLifecycleFinalizationRequestPayload {
    readonly gateway_lifecycle_payload_type: typeof GatewayLifecycleRequestType.CLOVER_LIFECYCLE_FINALIZATION;
    readonly request_details: CloverLifecycleFinalizationRequestDetails;
}

export interface CloverLifecyclePreflightRequestPayload {
    readonly gateway_lifecycle_payload_type: typeof GatewayLifecycleRequestType.CLOVER_LIFECYCLE_PREFLIGHT;
    readonly request_details: CloverLifecyclePreflightRequestDetails;
}

export interface ValorAdditionalPrompt {
    readonly PROMPT_NAME: string;
    readonly PROMPT_VALUE: string;
}

export interface ValorTerminalPublishPayloadV1 {
    readonly TRAN_MODE: string;
    readonly TRAN_CODE: string;
    readonly AMOUNT: string;
    readonly REQ_TXN_ID: string;
    readonly INVOICENUMBER?: string;
    readonly VAS_LABEL1?: string;
    readonly VAS_DATA1?: string;
    readonly VAS_LABEL2?: string;
    readonly VAS_DATA2?: string;
}

export interface ValorTerminalPublishPayloadV2
    extends ValorTerminalPublishPayloadV1 {
    readonly TIP_ENTRY?: '1';
}

export interface ValorTerminalPublishGatewayRequestV1 {
    readonly txn_type: 'vc_publish';
    readonly channel_id: string;
    readonly version: string;
    readonly payload: ValorTerminalPublishPayloadV1;
}

export interface ValorTerminalPublishGatewayRequestV2 {
    readonly txn_type: 'vc_publish';
    readonly channel_id: string;
    readonly version: string;
    readonly payload: ValorTerminalPublishPayloadV2;
}

export interface ValorCardTokenSaleGatewayRequest {
    readonly txn_type: 'sale';
    readonly amount: string;
    readonly surchargeIndicator: 0 | 1;
    readonly surchargeAmount: number;
    readonly token?: string;
    readonly invoicenumber: string;
    readonly cardholdername: string;
    readonly address?: string;
    readonly city?: string;
    readonly orderdescription?: string;
    readonly shipping_country?: string;
    readonly zip?: string;
    readonly additional_prompts?: readonly ValorAdditionalPrompt[];
}

export interface ValorVoidGatewayRequest {
    readonly txn_type: 'void';
    readonly ref_txn_id: string;
    readonly surchargeindicator: 1;
    readonly amount?: string;
}

export interface ValorRefundPrompt {
    readonly name: string;
    readonly value: string;
}

export interface ValorRefundGatewayRequest {
    readonly txn_type: 'refund';
    readonly amount: number;
    readonly surchargeIndicator: 0 | 1;
    readonly ref_txn_id: string;
    readonly sale_refund: '1';
    readonly merchantTransactionId: string;
    readonly additionalPrompts?: readonly ValorRefundPrompt[];
    readonly rrn?: string;
    readonly auth_code?: string;
    readonly child_epi?: string;
}

export interface ValorTransactionStatusGatewayRequest {
    readonly REQ_TXN_ID?: string;
    readonly TXN_ID?: string;
    readonly channel_id?: string;
}

export interface ValorTransactionListGatewayRequest {
    readonly date_filter?: string;
    readonly amount?: string;
    readonly ADDNL_VAL_1?: string;
}

export interface ValorReconciliationGatewayRequest {
    readonly gateway_txn_id: string;
    readonly merchant_transaction_id?: string;
    readonly amount?: string;
}

export interface AchPaymentGatewayRequest {
    readonly txn_type: 'ach';
    readonly account_id: string;
}

export interface IntegrationReconciliationGatewayRequest {
    readonly source: string;
}

export interface TerminalSaleDispatchTransactionDetailsSnapshot {
    readonly amount: number;
    readonly currency: string;
    readonly ipw_system_id: number;
    readonly transaction_details_type_id: number;
    readonly transaction_details_type_version: number;
    readonly [key: string]: JsonValue | undefined;
}

export interface TerminalSaleDispatchSourceDocumentKeySnapshot {
    readonly transaction_details_type_id: number;
    readonly transaction_details_type_version: number;
    readonly [key: string]: JsonValue | undefined;
}

export interface TerminalSaleDispatchGatewayRequest {
    readonly amount: string;
    readonly merchant_transaction_id: string;
    readonly source_document_key:
        TerminalSaleDispatchSourceDocumentKeySnapshot;
    readonly source_document_name: string;
    readonly source_document_reference: string;
    readonly transaction_details:
        TerminalSaleDispatchTransactionDetailsSnapshot;
}

export type GatewayLifecycleRequestVariant<
    TRequestType extends GatewayLifecycleRequestType,
    TGatewayRequest,
> = {
    readonly gateway_request_type: TRequestType;
    readonly gateway_request: TGatewayRequest;
};

export type ValorTerminalPublishGatewayLifecycleRequestV1 =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.VALOR_TERMINAL_PUBLISH_V1,
        ValorTerminalPublishGatewayRequestV1
    >;

export type ValorTerminalPublishGatewayLifecycleRequestV2 =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.VALOR_TERMINAL_PUBLISH_V2,
        ValorTerminalPublishGatewayRequestV2
    >;

export type ValorCardTokenSaleGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.VALOR_CARD_TOKEN_SALE,
        ValorCardTokenSaleGatewayRequest
    >;

export type ValorVoidGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.VALOR_VOID,
        ValorVoidGatewayRequest
    >;

export type ValorRefundGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.VALOR_REFUND,
        ValorRefundGatewayRequest
    >;

export type ValorTransactionStatusGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.VALOR_TRANSACTION_STATUS,
        ValorTransactionStatusGatewayRequest
    >;

export type ValorTransactionListGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.VALOR_TRANSACTION_LIST,
        ValorTransactionListGatewayRequest
    >;

export type ValorReconciliationGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.VALOR_RECONCILIATION,
        ValorReconciliationGatewayRequest
    >;

export type CardPointeCnpTokenSaleGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CARDPOINTE_CNP_TOKEN_SALE,
        CardPointeCnpTokenSaleRequestDetails
    >;

export type CardPointeTerminalSaleGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_SALE,
        CardPointeTerminalSaleRequestLifecyclePayload
    >;

export type CardPointeLifecyclePreflightGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_PREFLIGHT,
        CardPointeLifecyclePreflightRequestPayload
    >;

export type CardPointeLifecycleFinalizationGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_FINALIZATION,
        CardPointeLifecycleFinalizationRequestPayload
    >;

export type CardPointeTerminalTokenizationGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_TOKENIZATION,
        CardPointeTerminalTokenizationRequestDetails
    >;

export type CardPointeTransactionStatusGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CARDPOINTE_TRANSACTION_STATUS,
        CardPointeTransactionStatusRequestDetails
    >;

export type CardPointeVoidGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CARDPOINTE_VOID,
        CardPointeVoidRequestDetails
    >;

export type CardPointeRefundGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CARDPOINTE_REFUND,
        CardPointeRefundRequestDetails
    >;

export type CardPointeReconciliationGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CARDPOINTE_RECONCILIATION,
        CardPointeReconciliationLookupRequestDetails
    >;

export type CardPointeStoredCardSaleGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CARDPOINTE_STORED_CARD_SALE,
        CardPointeCnpTokenSaleRequestDetails
    >;

export type CloverCnpTokenSaleGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CLOVER_CNP_TOKEN_SALE,
        CloverCnpTokenSaleRequestDetails
    >;

export type CloverTerminalSaleGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CLOVER_TERMINAL_SALE,
        CloverTerminalSaleRequestLifecyclePayload
    >;

export type CloverLifecyclePreflightGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CLOVER_LIFECYCLE_PREFLIGHT,
        CloverLifecyclePreflightRequestPayload
    >;

export type CloverLifecycleFinalizationGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CLOVER_LIFECYCLE_FINALIZATION,
        CloverLifecycleFinalizationRequestPayload
    >;

export type CloverTerminalTokenizationGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CLOVER_TERMINAL_TOKENIZATION,
        CloverTerminalTokenizationRequestDetails
    >;

export type CloverTransactionStatusGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CLOVER_TRANSACTION_STATUS,
        CloverTransactionStatusRequestDetails
    >;

export type CloverVoidGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CLOVER_VOID,
        CloverVoidRequestDetails
    >;

export type CloverRefundGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CLOVER_REFUND,
        CloverRefundRequestDetails
    >;

export type CloverReconciliationGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CLOVER_RECONCILIATION,
        CloverReconciliationLookupRequestDetails
    >;

export type CloverStoredCardSaleGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.CLOVER_STORED_CARD_SALE,
        CloverCnpTokenSaleRequestDetails
    >;

export type AchPaymentGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.ACH_PAYMENT,
        AchPaymentGatewayRequest
    >;

export type IntegrationReconciliationGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.INTEGRATION_RECONCILIATION,
        IntegrationReconciliationGatewayRequest
    >;

export type TerminalSaleDispatchGatewayLifecycleRequest =
    GatewayLifecycleRequestVariant<
        typeof GatewayLifecycleRequestType.TERMINAL_SALE_DISPATCH,
        TerminalSaleDispatchGatewayRequest
    >;

// Discriminator: gateway_request_type
export type GatewayLifecycleRequest =
    | BinLookupGatewayLifecycleRequest
    | AchPaymentGatewayLifecycleRequest
    | CardPointeCnpTokenSaleGatewayLifecycleRequest
    | CardPointeLifecycleFinalizationGatewayLifecycleRequest
    | CardPointeLifecyclePreflightGatewayLifecycleRequest
    | CardPointeReconciliationGatewayLifecycleRequest
    | CardPointeRefundGatewayLifecycleRequest
    | CardPointeStoredCardSaleGatewayLifecycleRequest
    | CardPointeTerminalSaleGatewayLifecycleRequest
    | CardPointeTerminalTokenizationGatewayLifecycleRequest
    | CardPointeTransactionStatusGatewayLifecycleRequest
    | CardPointeVoidGatewayLifecycleRequest
    | CloverCnpTokenSaleGatewayLifecycleRequest
    | CloverLifecycleFinalizationGatewayLifecycleRequest
    | CloverLifecyclePreflightGatewayLifecycleRequest
    | CloverReconciliationGatewayLifecycleRequest
    | CloverRefundGatewayLifecycleRequest
    | CloverStoredCardSaleGatewayLifecycleRequest
    | CloverTerminalSaleGatewayLifecycleRequest
    | CloverTerminalTokenizationGatewayLifecycleRequest
    | CloverTransactionStatusGatewayLifecycleRequest
    | CloverVoidGatewayLifecycleRequest
    | IntegrationReconciliationGatewayLifecycleRequest
    | TerminalSaleDispatchGatewayLifecycleRequest
    | ValorCardTokenSaleGatewayLifecycleRequest
    | ValorReconciliationGatewayLifecycleRequest
    | ValorRefundGatewayLifecycleRequest
    | ValorTerminalPublishGatewayLifecycleRequestV1
    | ValorTerminalPublishGatewayLifecycleRequestV2
    | ValorTransactionListGatewayLifecycleRequest
    | ValorTransactionStatusGatewayLifecycleRequest
    | ValorVoidGatewayLifecycleRequest;

export type GatewayLifecycleRequestForType<
    TRequestType extends GatewayLifecycleRequestType,
> = Extract<
    GatewayLifecycleRequest,
    { readonly gateway_request_type: TRequestType }
>;
